import type { RoleId } from '@onuw/roles-data'
import { useEffect, useRef } from 'react'
import { create } from 'zustand'
import { vibrate } from '../lib/haptics'

/**
 * The enlarged card: a preview beside small cards on hover (mouse), or a full-screen view on a tap or a
 * long-press (touch) and a right-click. One layer for the whole app, mounted in root.tsx.
 */
type Anchor = { left: number; top: number; width: number; height: number }

interface ZoomState {
  role: RoleId | null
  mode: 'hover' | 'modal'
  anchor: Anchor | null
  openedAt: number
  show: (role: RoleId, mode: 'hover' | 'modal', anchor?: Anchor | null) => void
  hide: (mode?: 'hover' | 'modal') => void
}

export const useCardZoom = create<ZoomState>((set, get) => ({
  role: null,
  mode: 'hover',
  anchor: null,
  openedAt: 0,
  show: (role, mode, anchor = null) => set({ role, mode, anchor, openedAt: Date.now() }),
  hide: (mode) => {
    if (!mode || get().mode === mode) set({ role: null, anchor: null })
  },
}))

/** Cards narrower than this get a hover preview (their ability text is small or hidden). */
const HOVER_BELOW = 170
const HOVER_DELAY = 380
const LONG_PRESS = 450
const MOVE_TOLERANCE = 10
/** A card inside one of these has a tap of its own (a seat to pick, a role to add): only a long-press enlarges it. */
const TAPPABLE = 'a[href], button:not(:disabled), [role="button"], label'

/**
 * Pointer handlers that open the enlarged card; spread them on the card's root element. On touch a tap opens it
 * too, unless the card is inside something tappable. `hover: false` keeps the tap, the long-press and the
 * right-click but drops the hover preview (where the whole card is already on screen).
 */
export function useZoomTrigger(role: RoleId | undefined, { hover = true }: { hover?: boolean } = {}) {
  const ref = useRef<HTMLDivElement>(null)
  const timer = useRef<number | undefined>(undefined)
  const origin = useRef<{ x: number; y: number } | null>(null)
  const swallowClick = useRef(false)
  // the pointer that pressed last: a tap opens the card for a finger or a pen, never for the mouse
  const pointer = useRef<string | null>(null)
  const clear = () => {
    if (timer.current !== undefined) window.clearTimeout(timer.current)
    timer.current = undefined
  }
  // unmount: drop a pending hover / long-press timer
  useEffect(
    () => () => {
      if (timer.current !== undefined) window.clearTimeout(timer.current)
    },
    [],
  )

  if (!role) return { ref, handlers: {} }
  const zoom = useCardZoom.getState
  const handlers = {
    onPointerEnter: (e: React.PointerEvent) => {
      if (!hover || e.pointerType !== 'mouse') return
      const el = ref.current
      if (!el || el.getBoundingClientRect().width >= HOVER_BELOW) return
      clear()
      timer.current = window.setTimeout(() => {
        const r = el.getBoundingClientRect()
        zoom().show(role, 'hover', { left: r.left, top: r.top, width: r.width, height: r.height })
      }, HOVER_DELAY)
    },
    onPointerLeave: (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      clear()
      zoom().hide('hover')
    },
    onPointerDown: (e: React.PointerEvent) => {
      swallowClick.current = false
      pointer.current = e.pointerType
      clear()
      if (e.pointerType === 'mouse') {
        zoom().hide('hover')
        return
      }
      origin.current = { x: e.clientX, y: e.clientY }
      timer.current = window.setTimeout(() => {
        timer.current = undefined
        swallowClick.current = true
        vibrate(12)
        zoom().show(role, 'modal')
      }, LONG_PRESS)
    },
    onPointerMove: (e: React.PointerEvent) => {
      const o = origin.current
      if (!o || timer.current === undefined) return
      if (Math.hypot(e.clientX - o.x, e.clientY - o.y) > MOVE_TOLERANCE) clear()
    },
    onPointerUp: () => {
      clear()
      origin.current = null
    },
    onPointerCancel: () => {
      clear()
      origin.current = null
    },
    // a long-press must not also select the seat or card underneath
    onClickCapture: (e: React.MouseEvent) => {
      if (!swallowClick.current) return
      swallowClick.current = false
      e.preventDefault()
      e.stopPropagation()
    },
    // touch: a tap on a card that has nothing else to do (a swipe that scrolls sends no click)
    onClick: () => {
      if (pointer.current !== 'touch' && pointer.current !== 'pen') return
      if (ref.current?.parentElement?.closest(TAPPABLE)) return
      zoom().show(role, 'modal')
    },
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault()
      if (!swallowClick.current) zoom().show(role, 'modal')
    },
  }
  return { ref, handlers }
}
